/*
 * Liquid contract: <andcharlie-size-finder data-chart-anchor="#chart-id">
 *   <script type="application/json" data-ac-size-payload>{system,preference,product}</script>
 *   <form data-ac-fit-form><div data-ac-fit-body-fields>weightKg + shortBody</div>
 *     <div data-ac-fit-measurements></div><button type="submit">…</button></form>
 *   <div data-ac-fit-result aria-live="polite"></div>
 * </andcharlie-size-finder>
 * The engine alone chooses candidates and rule-dependent questions. The owner
 * choosing unknown reveals the requested auxiliary length UI without inferring
 * body shape or a numerical boundary. Profiles contain dog inputs, never product
 * rankings. Optional account transport is disabled until its authenticated app is installed.
 */
if (!customElements.get('andcharlie-size-finder')) {
  customElements.define('andcharlie-size-finder', class extends HTMLElement {
    connectedCallback() {
      if (this.acSizeRelocating) return;
      this.controller?.abort();
      this.controller = new AbortController();
      this.engineInitialized = false;
      this.profileRestored = false;
      this.records = null;
      this.form = this.querySelector('[data-ac-fit-form]');
      this.validationMessage = this.querySelector('[data-ac-fit-error]');
      this.validationShown = false;
      this.result = this.querySelector('[data-ac-fit-result]');
      this.measurements = this.querySelector('[data-ac-fit-measurements]');
      this.bodyFields = this.querySelector('[data-ac-fit-body-fields]');
      this.unknownFields = this.querySelector('[data-ac-fit-unknown-fields]');
      this.lengthOptional = this.querySelector('[data-ac-fit-length-optional]');
      this.profileNotice = this.querySelector('[data-ac-profile-notice]');
      this.identity = this.dataset.customerId || 'guest';
      this.dirty = false;
      if (!this.form || !this.result) return;

      this.result.setAttribute('aria-live', 'polite');
      this.result.setAttribute('aria-atomic', 'true');
      try {
        this.payload = JSON.parse(this.querySelector('[data-ac-size-payload]')?.textContent || 'null');
      } catch (_) {
        this.payload = null;
      }
      if (!this.payload?.system) {
        this.form.hidden = true;
        this.showFallback();
        return;
      }
      if (!this.initializeEngine()) {
        this.form.hidden = true;
        this.showFallback();
        // Dawn/Theme Editor reinject external scripts dynamically. Those scripts
        // can execute out of source order even when their defer attributes remain.
        // Retry only on an existing engine script's load event, never poll or fetch.
        document.querySelectorAll('script[src]').forEach((script) => {
          let isEngine = false;
          try { isEngine = new URL(script.src, document.baseURI).pathname.endsWith('/andcharlie-size-engine.js'); }
          catch (_) { /* Invalid script URLs cannot supply this dependency. */ }
          if (isEngine) script.addEventListener('load', () => this.initializeEngine(), {
            once: true, signal: this.controller.signal,
          });
        });
      }
    }

    initializeEngine() {
      if (this.engineInitialized) return true;
      if (!this.isConnected || this.controller.signal.aborted) return false;
      this.engine = window.AndCharlieSizeEngine;
      if (typeof this.engine?.resolveFit !== 'function') return false;
      this.engineInitialized = true;
      const options = { signal: this.controller.signal };
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.form.addEventListener('input', (event) => this.inputChanged(event), options);
      this.form.addEventListener('change', (event) => this.inputChanged(event), options);
      this.form.addEventListener('focusin', () => this.start(), options);
      this.form.addEventListener('reset', () => this.clearValidation(), options);
      // Allow submit to reach our field-level checks before native radio/auxiliary validation.
      if (this.requiresBodyMeasurements()) this.form.noValidate = true;
      this.result.replaceChildren();
      this.clearValidation();
      this.prepareForm();
      this.restoreProfile();
      document.querySelectorAll('script[src]').forEach((script) => {
        if (script.src.includes('/andcharlie-pet-profile.js')) script.addEventListener('load', () => this.restoreProfile(), {once:true, signal:this.controller.signal});
      });
      return true;
    }

    disconnectedCallback() {
      if (this.acSizeRelocating) return;
      this.controller?.abort();
    }

    prepareForm() {
      // An empty profile can reveal only the engine's explicit data/selection state.
      // Do not treat a missing initial answer as a broken configuration.
      const initial = this.resolve({});
      if (initial?.status === 'no_size') {
        this.hidden = true;
        return;
      }
      if (['invalid', 'no_match'].includes(initial?.status) || !initial ||
          !this.engine.validateSystem(this.payload.system).valid ||
          (this.payload.product && !this.engine.validateProduct(this.payload.system, this.payload.product).valid)) {
        this.form.hidden = true;
        this.showFallback();
        return;
      }
      this.hidden = false;
      this.form.hidden = false;
      const measurementBased = this.payload.system.method === 'measurement';
      if (this.bodyFields) {
        this.bodyFields.hidden = measurementBased;
        this.bodyFields.querySelectorAll('input').forEach((input) => {
          input.disabled = measurementBased;
        });
      }
      this.syncUnknownFields();
      if (measurementBased) {
        if (initial.status !== 'needs_input' || !this.showQuestions(initial.requestedFields, false)) {
          this.form.hidden = true;
          this.showFallback();
        }
      }
    }

    resolve(profile) {
      try {
        return this.engine.resolveFit(profile, this.payload.system, this.payload.preference, this.payload.product);
      } catch (_) {
        return null;
      }
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.dispatchEvent(new CustomEvent('ac:fit-start', {
        bubbles: true,
        detail: {
          systemKey: this.payload.system.key,
          ruleRevision: this.payload.system.revision || null,
        },
      }));
    }

    inputChanged(event) {
      if (!event.target.matches('input')) return;
      const revalidate = this.validationShown;
      this.start();
      this.dirty = true;
      this.result.replaceChildren();
      this.highlight();
      if (['weightKg', 'shortBody'].includes(event.target.name)) {
        this.measurements?.replaceChildren();
        const length = this.unknownFields?.querySelector('input');
        if (length) length.required = false;
        if (this.lengthOptional) this.lengthOptional.hidden = false;
        this.syncUnknownFields();
      }
      if (revalidate) this.validateBodyMeasurements(false);
    }

    syncUnknownFields() {
      if (!this.unknownFields) return;
      const unknown = this.payload.system.method === 'body' && new FormData(this.form).get('shortBody') === 'unknown';
      this.unknownFields.hidden = !unknown;
      const input = this.unknownFields.querySelector('input');
      if (input) {
        input.disabled = !unknown;
        input.required = unknown && this.requiresUnknownLength();
        input.setAttribute('aria-required', String(input.required));
        const requiredMarker = this.unknownFields.querySelector('[data-ac-fit-length-required]');
        if (requiredMarker) requiredMarker.hidden = !input.required;
        if (this.lengthOptional) this.lengthOptional.hidden = input.required;
      }
      if (!unknown && this.lengthOptional) this.lengthOptional.hidden = false;
    }

    profile() {
      const data = new FormData(this.form);
      const profile = {};
      for (const field of ['weightKg', 'lengthCm', 'chestCm', 'neckCm']) {
        const value = data.get(field);
        if (typeof value === 'string' && value.trim() !== '') profile[field] = this.engine.parseNumber(value);
      }
      if (data.has('shortBody')) profile.shortBody = data.get('shortBody');
      return profile;
    }

    requiresBodyMeasurements() {
      return this.dataset.acRequiredBody === 'true' && this.payload.system.method === 'body';
    }

    requiresUnknownLength() {
      if (!this.requiresBodyMeasurements()) return false;
      const profile = this.profile();
      if (this.payload.system.method !== 'body' || profile.shortBody !== 'unknown') return false;
      return this.engine.resolveBody({ ...profile, lengthCm: undefined }, this.payload.system)
        .requestedFields.includes('lengthCm');
    }

    invalidBodyMeasurements() {
      if (!this.requiresBodyMeasurements()) return [];
      const fields = ['weightKg', 'neckCm', 'chestCm'];
      if (this.requiresUnknownLength() && new FormData(this.form).get('shortBody') === 'unknown') fields.push('lengthCm');
      return fields.map((field) => {
        const input = this.form.querySelector(`[name="${field}"]`);
        const value = this.engine.parseNumber(input?.value);
        const bounds = this.payload.system.rules?.inputBounds?.[field];
        return { input, missing: !input?.value.trim(), invalid: !input || input.disabled ||
          !Number.isFinite(value) || value <= 0 || (bounds && (value < bounds.min || value > bounds.max)) };
      }).filter((entry) => entry.invalid);
    }

    clearValidation() {
      this.validationShown = false;
      if (this.validationMessage) this.validationMessage.textContent = '';
      for (const field of ['weightKg', 'neckCm', 'chestCm', 'lengthCm']) {
        this.form.querySelector(`[name="${field}"]`)?.removeAttribute('aria-invalid');
      }
    }

    validateBodyMeasurements(focus) {
      const invalid = this.invalidBodyMeasurements();
      this.clearValidation();
      if (!invalid.length) return true;
      this.validationShown = true;
      invalid.forEach(({ input }) => input?.setAttribute('aria-invalid', 'true'));
      const marker = this.dataset.context === 'product' ? '必須' : this.dataset.acGuideForm === 'true' ? '✓' : '※';
      if (this.validationMessage) this.validationMessage.textContent = invalid.some(({ missing }) => missing)
        ? `${marker}の項目をすべて入力してください`
        : `${marker}の項目を正しい数値で入力してください`;
      if (focus) invalid[0].input?.focus();
      return false;
    }

    submit(event) {
      event.preventDefault();
      this.start();
      this.highlight();
      this.result.replaceChildren();
      if (!this.validateBodyMeasurements(true)) return;
      if (!this.form.reportValidity()) return;
      const profile = this.profile();
      const resolved = this.resolve(profile);
      if (this.payload.system.method === 'body' && resolved && resolved.status !== 'invalid') this.rememberProfile(profile);
      if (resolved?.status === 'needs_input') {
        this.result.replaceChildren();
        if (!this.showQuestions(resolved.requestedFields, true)) this.showFallback();
        return;
      }
      if (resolved?.status !== 'ready' || !this.showResult(resolved)) {
        this.showFallback(resolved);
      }
    }

    sessionStorage() { try { return window.sessionStorage; } catch (_) { return null; } }

    restoreProfile() {
      if (this.payload.system.method !== 'body' || this.form.hidden) return;
      const profiles = window.AndCharliePetProfile;
      if (!profiles || this.profileRestored) return;
      this.profileRestored = true;
      const saved = profiles?.read(this.sessionStorage(), this.identity);
      if (saved) this.fillProfile(saved, '');
      window.AndCharlieSizeRecords?.attach(this);
      document.querySelectorAll('script[src]').forEach((script) => {
        if (script.src.includes('/andcharlie-size-records.js')) script.addEventListener('load', () => window.AndCharlieSizeRecords?.attach(this), {once:true, signal:this.controller.signal});
      });
    }

    fillProfile(profile, notice) {
      if (this.dirty) return;
      this.clearValidation();
      this.measurements?.replaceChildren();
      this.highlight();
      this.result.replaceChildren();
      for (const input of this.form.querySelectorAll('input')) {
        if (input.type === 'radio') input.checked = input.value === profile[input.name];
        else if (input.type !== 'checkbox') input.value = profile[input.name] ?? '';
      }
      this.syncUnknownFields();
      // Older cards can contain only weight. Restore their inputs, never a partial result.
      if (this.invalidBodyMeasurements().length) {
        this.setProfileNotice(notice);
        const details = this.closest('details');
        if (details) details.open = true;
        return;
      }
      // Ask only fields required by this revision, then restore their saved values.
      let result = this.resolve(this.profile());
      for (let step = 0; step < 3 && result?.status === 'needs_input'; step += 1) {
        if (!this.showQuestions(result.requestedFields, false)) break;
        for (const input of this.form.querySelectorAll('input')) {
          if (!['radio', 'checkbox'].includes(input.type) && profile[input.name] != null) input.value = profile[input.name];
        }
        result = this.resolve(this.profile());
      }
      if (result?.status === 'ready') this.showResult(result);
      else if (result?.status === 'no_rules') this.showFallback();
      this.setProfileNotice(notice);
      const details = this.closest('details');
      if (details) details.open = true;
    }

    setProfileNotice(message) {
      if (!this.profileNotice) return;
      this.profileNotice.replaceChildren();
      const text = document.createElement('span');
      text.textContent = message;
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'link underlined-link';
      clear.textContent = '入力をクリア';
      clear.addEventListener('click', () => {
        window.AndCharliePetProfile?.clear(this.sessionStorage());
        this.dirty = true;
        this.form.reset();
        this.measurements?.replaceChildren();
        this.syncUnknownFields();
        this.result.replaceChildren();
        this.highlight();
        this.profileNotice.replaceChildren();
        this.dispatchEvent(new CustomEvent('ac:fit-clear'));
      }, {signal: this.controller.signal});
      this.profileNotice.append(...(message ? [text, clear] : [clear]));
    }

    rememberProfile(profile) {
      if (window.AndCharliePetProfile?.write(this.sessionStorage(), this.identity, profile)) {
        this.setProfileNotice('');
      }
      if (this.dataset.accountEnabled === 'true' && this.payload.system.method === 'body' && typeof this.records?.save !== 'function') {
        const panel = this.querySelector('[data-ac-records]');
        if (panel) {
          panel.hidden = false;
          panel.dataset.saveState = 'failed';
          panel.dataset.saveError = 'missing_autosave';
          panel.querySelector('[data-ac-records-status]').textContent = '現在、サイズの保存に接続できません。判定はご利用いただけます。';
        }
        return;
      }
      this.records?.save?.(profile);
    }

    highlight(resolved) {
      const anchor = this.dataset.chartAnchor;
      if (!anchor?.startsWith('#')) return;
      const chart = document.getElementById(anchor.slice(1));
      chart?.querySelectorAll('[data-ac-size-key]').forEach((row) => {
        row.removeAttribute('data-ac-fit-rank');
        row.querySelector('[data-ac-fit-row-label]')?.remove();
        if (!resolved?.baseCandidates?.includes(row.dataset.acSizeKey)) return;
        const preferred = resolved.preferredKey === row.dataset.acSizeKey || resolved.baseCandidates.length === 1;
        row.dataset.acFitRank = preferred ? 'primary' : resolved.preferredKey ? 'comparison' : 'equal';
        const label = document.createElement('span');
        label.dataset.acFitRowLabel = '';
        label.className = 'ac-size-foundation__candidate-label';
        label.textContent = this.dataset.acFitReference === 'reference' ? '目安' : preferred ? 'おすすめ' : resolved.preferredKey ? '候補' : '同じく候補';
        row.querySelector('th')?.append(label);
      });
    }

    showQuestions(fields, focus) {
      const labels = { lengthCm: '着丈', chestCm: '胴回り', neckCm: '首回り' };
      if (!this.measurements || !Array.isArray(fields) || fields.length === 0 ||
          fields.some((field) => !Object.prototype.hasOwnProperty.call(labels, field))) return false;
      let firstInput;
      for (const field of new Set(fields)) {
        let input = field === 'lengthCm' && this.payload.system.method === 'body' ? this.unknownFields?.querySelector('input') : null;
        if (input) {
          // A body-length question is available only after the owner's unknown answer.
          if (input.disabled) return false;
          input.required = true;
          if (this.lengthOptional) this.lengthOptional.hidden = true;
        }
        const existing = this.form.querySelector(`[name="${field}"]`);
        input ||= existing && !existing.disabled ? existing : null;
        if (!input) {
          const label = document.createElement('label');
          label.className = 'ac-size-finder__step';
          const title = document.createElement('span');
          title.className = 'ac-size-finder__label';
          title.textContent = labels[field];
          const number = document.createElement('span');
          number.className = 'ac-size-finder__number';
          input = document.createElement('input');
          input.type = 'text';
          input.name = field;
          input.min = '0';
          input.step = 'any';
          input.inputMode = 'decimal';
          input.required = true;
          input.autocomplete = 'off';
          const unit = document.createElement('span');
          unit.className = 'ac-size-finder__unit';
          unit.textContent = 'cm';
          number.append(input, unit);
          label.append(title, number);
          this.measurements.append(label);
        }
        input.required = true;
        firstInput ||= input;
      }
      if (focus) {
        this.appendText('追加の採寸値を入力してください。');
        firstInput?.focus();
      }
      return true;
    }

    labelFor(row) {
      const values = this.payload.product?.optionValues;
      if (!Array.isArray(values)) return row.label;
      const matches = (row.optionValues || []).filter((value) => values.includes(value));
      return matches.length === 1 ? matches[0] : null;
    }

    showResult(resolved) {
      const rows = this.payload.system.chart?.rows;
      const keys = resolved.rankedCandidates;
      const base = resolved.baseCandidates;
      if (!Array.isArray(rows) || !Array.isArray(keys) || !Array.isArray(base) ||
          keys.length < 1 || keys.length > 2 || new Set(keys).size !== keys.length ||
          keys.some((key) => !base.includes(key))) return false;
      const candidates = keys.map((key) => rows.find((row) => row.key === key));
      if (candidates.some((row) => !row)) return false;
      const labels = candidates.map((row) => this.labelFor(row));
      if (labels.some((label) => typeof label !== 'string' || label.trim() === '')) return false;
      const preferredIndex = resolved.preferredKey == null ? -1 : keys.indexOf(resolved.preferredKey);
      if (resolved.preferredKey != null && preferredIndex < 0) return false;

      this.result.replaceChildren();
      const title = this.dataset.acFitReference === 'reference' ? 'サイズの目安' : preferredIndex >= 0 || labels.length === 1 ? 'おすすめサイズ' : 'サイズ候補';
      this.appendText(title, 'ac-size-finder__result-label');
      this.appendText(preferredIndex >= 0 ? labels[preferredIndex] : labels.join(' / '), 'ac-size-finder__summary');
      if (preferredIndex >= 0 && labels.length === 2) this.appendText(`もうひとつの候補：${labels[1 - preferredIndex]}`, 'ac-size-finder__secondary');
      this.highlight(resolved);
      this.appendChartLink();

      // Stock is a separate, read-only display of the same candidates.
      const availability = this.closest('[data-ac-size-panel]') ? null : this.payload.product?.availableByLabel;
      const unavailable = labels.filter((label) => availability?.[label] === false);
      if (unavailable.length) {
        this.appendText(`${unavailable.join(' / ')}：現在在庫切れです。`, 'ac-size-finder__availability');
      }
      this.dispatchEvent(new CustomEvent('ac:fit-result', {
        bubbles: true,
        detail: {
          systemKey: resolved.systemKey,
          ruleRevision: resolved.revision,
          candidateKeys: [...keys],
          baseCandidateKeys: [...base],
          preferredKey: resolved.preferredKey || null,
        },
      }));
      return true;
    }

    appendText(text, className) {
      const paragraph = document.createElement('p');
      if (className) paragraph.className = className;
      paragraph.textContent = text;
      this.result.append(paragraph);
    }

    appendChartLink() {
      if (this.dataset.acGuideForm === 'true') return;
      const anchor = this.dataset.chartAnchor;
      if (typeof anchor !== 'string' || !anchor.startsWith('#') || anchor.length < 2) return;
      const link = document.createElement('a');
      link.href = anchor;
      link.className = 'link underlined-link';
      link.textContent = 'サイズ表を見る';
      link.addEventListener('click', () => {
        const chart = document.getElementById(anchor.slice(1));
        const details = chart?.closest('details');
        if (details) details.open = true;
      }, { signal: this.controller.signal });
      this.result.append(link);
    }

    showFallback(resolved) {
      this.highlight();
      this.result.replaceChildren();
      if (resolved?.reason === 'invalid_input') {
        const labels = {weightKg:'体重',neckCm:'首回り',chestCm:'胴回り',lengthCm:'着丈'};
        this.appendText(`${labels[resolved.invalidField] || '入力値'}を正しい数値で入力してください。例：13.5、１３、５`);
        return;
      }
      if (resolved?.status === 'no_match' || resolved?.reason === 'input_out_of_bounds') {
        this.appendText(resolved.reason === 'body_shape_unresolved' ? '体型を確認してサイズをご案内します。ご相談ください。' : '個別にサイズをご案内します。入力した数値を添えてご相談ください。');
        const link = document.createElement('a');
        link.href = this.dataset.contactUrl || '/pages/contact-us';
        link.className = 'link underlined-link';
        link.textContent = 'サイズについて相談する';
        this.result.append(link);
        return;
      }
      this.appendText('サイズ表をご確認ください。');
      this.appendChartLink();
    }
  });
}
